import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import type { UseQueryResult } from "@tanstack/react-query";
import { Check, Droplets, Equal, Pencil, Plus, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  useCreateRefill,
  useDeleteRefill,
  useRefills,
  useSiteGenerators,
  useSites,
  useSiteTanks,
  useUpdateRefill,
} from "@/api/hooks";
import { ApiError } from "@/api/problem";
import type { Refill, RefillStatus } from "@/api/types";
import { canEditRefill } from "@/auth/rbac";
import { useSession } from "@/auth/session";
import { PageHeader } from "@/components/app-shell";
import { Dialog } from "@/components/ui/dialog";
import { DataTable } from "@/components/ui/data-table";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Field,
  Input,
  MicroLabel,
  Num,
  NumberInput,
  Textarea,
} from "@/components/ui/primitives";
import { NONE, Select } from "@/components/ui/select";
import { SightGlass } from "@/components/ui/sight-glass";
import { EmptyState, FormError } from "@/components/ui/states";
import {
  checkRefill,
  isSubmittable,
  issueFor,
  type RefillCheck,
  type RefillContext,
  type RefillDraft,
  type RefillField,
  type RefillIssueCode,
} from "@/domain/refill";
import { formatDateTime, formatHours, formatLiters, formatNumber, toDateTimeLocalValue } from "@/lib/format";
import { useI18n } from "@/i18n/provider";
import { cn } from "@/lib/utils";

const STATUS_TONE: Record<RefillStatus, "ok" | "low" | "idle"> = {
  completed: "ok",
  pending: "low",
  cancelled: "idle",
};

/** A completed refill is the fuel ledger. It is amended by nobody, through no screen. */
const isClosed = (refill: Refill) =>
  refill.status === "completed" || refill.status === "cancelled";

/**
 * The instant after the end of the given day, in the reader's own timezone.
 *
 * `recorded_to` is EXCLUSIVE on the server. A person who filters "to the 14th" means "include
 * the 14th" — so the boundary sent is the 15th at 00:00. Sending the 14th at 00:00 would drop
 * every refill recorded that day and quietly under-report a week.
 */
function nextMidnight(day: string): string {
  const date = new Date(`${day}T00:00:00`);
  date.setDate(date.getDate() + 1);
  return date.toISOString();
}

/* ------------------------------------------------------------ the table */

export function RefillsTable({
  query,
  empty,
  hideSiteColumn,
  rowActions,
}: {
  query: UseQueryResult<{ data: Refill[] }, unknown>;
  empty: ReactNode;
  hideSiteColumn?: boolean;
  /** Amend / delete, on the refills ledger itself. Off elsewhere — a site page is a read. */
  rowActions?: boolean;
}) {
  const { t, lang } = useI18n();

  const columns = useMemo<ColumnDef<Refill, unknown>[]>(() => {
    const cols: ColumnDef<Refill, unknown>[] = [
      {
        accessorKey: "recorded_at",
        header: () => t("refills.recordedAt"),
        cell: ({ row }) => (
          <Num className="text-xs">{formatDateTime(lang, row.original.recorded_at)}</Num>
        ),
      },
    ];

    if (!hideSiteColumn) {
      cols.push({
        id: "site",
        header: () => t("refills.site"),
        cell: ({ row }) => (
          <Link
            to="/sites/$siteId"
            params={{ siteId: row.original.site.id }}
            className="font-medium text-text underline-offset-4 hover:underline"
          >
            {row.original.site.name}
          </Link>
        ),
      });
    }

    cols.push(
      {
        accessorKey: "before_liters",
        header: () => t("refills.before"),
        cell: ({ row }) => <Num>{formatLiters(lang, row.original.before_liters)}</Num>,
      },
      {
        accessorKey: "added_liters",
        header: () => t("refills.added"),
        // The added column is the one an auditor reads first. It carries the weight.
        cell: ({ row }) => (
          <Num className="font-medium text-text">
            +{formatLiters(lang, row.original.added_liters)}
          </Num>
        ),
      },
      {
        accessorKey: "after_liters",
        header: () => t("refills.after"),
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            <Num>{formatLiters(lang, row.original.after_liters)}</Num>
            <span className="font-mono text-[10px] text-faint">
              <Num>
                {Math.round(
                  (row.original.after_liters / row.original.tank_capacity_liters) * 100,
                )}
                %
              </Num>
            </span>
          </span>
        ),
      },
      {
        accessorKey: "working_hours",
        header: () => t("refills.workingHours"),
        cell: ({ row }) => (
          <Num className="text-muted">{formatHours(lang, row.original.working_hours)}</Num>
        ),
      },
      {
        id: "recorded_by",
        header: () => t("refills.recordedBy"),
        cell: ({ row }) => (
          <span className="text-xs text-muted">{row.original.recorded_by.name}</span>
        ),
      },
      {
        accessorKey: "status",
        header: () => t("refills.status"),
        cell: ({ row }) => (
          <Badge tone={STATUS_TONE[row.original.status]}>
            {t(`refills.status.${row.original.status}` as const)}
          </Badge>
        ),
      },
    );

    if (rowActions) {
      cols.push({
        id: "actions",
        header: () => <span className="sr-only">{t("common.actions")}</span>,
        cell: ({ row }) => <RowActions refill={row.original} />,
      });
    }

    return cols;
  }, [t, lang, hideSiteColumn, rowActions]);

  return (
    <DataTable
      caption={t("refills.title")}
      columns={columns}
      data={query.data?.data}
      isLoading={query.isPending}
      error={query.error}
      onRetry={() => void query.refetch()}
      empty={empty}
    />
  );
}

/* --------------------------------------------------------- row actions */

/**
 * What may be done to a refill that is already recorded.
 *
 * A COMPLETED refill is the fuel ledger — an audited record. The server refuses to amend its
 * figures (409) and refuses to delete it outright (409, "cancel it instead"). So neither
 * control is rendered for one: a button that always fails is not an affordance, it is a lie.
 *
 * Amending is not a plain role check. The spec says "owner, admin, or the operator WHO
 * RECORDED IT" — row ownership, which no role can express. `canEditRefill` is the one place
 * that rule lives, and this screen defers to it rather than restating it and drifting.
 */
function RowActions({ refill }: { refill: Refill }) {
  const { t } = useI18n();
  const { can, role, me, readOnly } = useSession();

  const [amending, setAmending] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const closed = isClosed(refill);
  const mayAmend =
    !readOnly &&
    !closed &&
    canEditRefill(role, refill.recorded_by.user_id, me?.user.id ?? "");
  const mayDelete = can("refills:delete") && refill.status !== "completed";

  if (!mayAmend && !mayDelete) {
    // A viewer, or a closed record: say why there is nothing here rather than leaving a hole.
    return closed ? (
      <span className="sr-only">{t("refills.closedRecord")}</span>
    ) : null;
  }

  return (
    <div className="flex items-center justify-end gap-1">
      {mayAmend ? (
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("refills.amend")}
          title={t("refills.amend")}
          onClick={() => setAmending(true)}
        >
          <Pencil aria-hidden />
        </Button>
      ) : null}

      {mayDelete ? (
        // NOT `danger`. `dry` is the colour of fuel running out and of nothing else — a
        // delete button wearing it teaches the eye to ignore the one warning that costs money.
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("common.delete")}
          title={t("common.delete")}
          onClick={() => setDeleting(true)}
        >
          <Trash2 aria-hidden />
        </Button>
      ) : null}

      {amending ? (
        <AmendRefillDialog refill={refill} open={amending} onOpenChange={setAmending} />
      ) : null}
      {deleting ? (
        <DeleteRefillDialog refill={refill} open onOpenChange={setDeleting} />
      ) : null}
    </div>
  );
}

/** Amend a PENDING refill — the same arithmetic, the same wall, against the same capacity. */
function AmendRefillDialog({
  refill,
  open,
  onOpenChange,
}: {
  refill: Refill;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const updateRefill = useUpdateRefill();

  // The capacity is snapshotted onto the refill as it was when recorded — so the ceiling the
  // amendment is checked against is the one that actually applied, not today's.
  const context: RefillContext = { capacityLiters: refill.tank_capacity_liters };

  const figures = useRefillFigures(context, {
    before: refill.before_liters,
    added: refill.added_liters,
    after: refill.after_liters,
    hours: refill.working_hours,
  });

  const [notes, setNotes] = useState(refill.notes ?? "");
  const [status, setStatus] = useState<RefillStatus>(refill.status);

  const serverErrors =
    updateRefill.error instanceof ApiError ? updateRefill.error.fieldErrors : {};
  const errorFor = useFieldError(figures.check, context, serverErrors);

  const { draft } = figures;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("refills.amendTitle")}
      description={refill.site.name}
      className="w-[min(40rem,calc(100vw-2rem))]"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={!figures.canSubmit || updateRefill.isPending}
            onClick={() =>
              updateRefill.mutate(
                {
                  refillId: refill.id,
                  before_liters: draft.before_liters!,
                  added_liters: draft.added_liters!,
                  after_liters: draft.after_liters!,
                  working_hours: draft.working_hours!,
                  status,
                  notes: notes.trim(),
                },
                { onSuccess: () => onOpenChange(false) },
              )
            }
          >
            {updateRefill.isPending ? t("common.saving") : t("common.save")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormError
          error={
            updateRefill.error instanceof ApiError && updateRefill.error.isFormLevel
              ? updateRefill.error
              : null
          }
        />

        <p className="text-xs text-faint">{t("refills.amendHint")}</p>

        <ArithmeticFields figures={figures} errorFor={errorFor} />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t("refills.workingHours")}
            error={errorFor("working_hours")}
            required
          >
            {(props) => (
              <NumberInput
                {...props}
                required
                min={0}
                step="0.1"
                value={figures.hours}
                onChange={(e) => figures.setHours(e.target.value)}
              />
            )}
          </Field>

          <Field label={t("refills.amendStatus")} required>
            {(props) => (
              <Select
                {...props}
                value={status}
                onValueChange={(value) => setStatus(value as RefillStatus)}
                options={[
                  { value: "pending", label: t("refills.status.pending") },
                  { value: "completed", label: t("refills.status.completed") },
                  { value: "cancelled", label: t("refills.status.cancelled") },
                ]}
              />
            )}
          </Field>
        </div>

        {/* No capacity line here: the over-capacity error already names the ceiling, in
            liters, at the moment it matters. Saying it twice is furniture. (DESIGN.md §7) */}
        <Field label={t("refills.notes")} hint={t("common.optional")}>
          {(props) => (
            <Textarea
              {...props}
              value={notes}
              placeholder={t("refills.notesPlaceholder")}
              onChange={(e) => setNotes(e.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

function DeleteRefillDialog({
  refill,
  open,
  onOpenChange,
}: {
  refill: Refill;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, lang } = useI18n();
  const deleteRefill = useDeleteRefill();

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("refills.deleteConfirm")}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          {/* `secondary`, not `danger`: see DESIGN.md §1. The weight is in the words. */}
          <Button
            variant="secondary"
            disabled={deleteRefill.isPending}
            onClick={() =>
              deleteRefill.mutate(refill.id, { onSuccess: () => onOpenChange(false) })
            }
          >
            {deleteRefill.isPending ? t("common.saving") : t("common.delete")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <FormError error={deleteRefill.error} />

        <p className="text-sm text-muted">{t("refills.deleteBody")}</p>

        <div className="rounded-[6px] border border-rule bg-paper-sunk p-3 text-sm">
          <p className="font-medium text-text">{refill.site.name}</p>
          <p className="mt-1 font-mono text-xs text-muted">
            <Num>
              +{formatLiters(lang, refill.added_liters)} {t("common.liters")}
            </Num>{" "}
            · <Num>{formatDateTime(lang, refill.recorded_at)}</Num>
          </p>
        </div>
      </div>
    </Dialog>
  );
}

/* ------------------------------------------------------------ list page */

export function RefillsPage() {
  const { t } = useI18n();
  const { can } = useSession();

  const [siteId, setSiteId] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");

  const sitesQuery = useSites({ limit: 100 });
  const refillsQuery = useRefills({
    ...(siteId ? { site_id: siteId } : {}),
    ...(status ? { status: status as RefillStatus } : {}),
    // A date box is a DAY; the API takes an instant. `to` is exclusive on the server, so it
    // is pushed to the following midnight — otherwise "to: the 14th" silently drops every
    // refill recorded on the 14th, which is the day the person is most likely asking about.
    ...(from ? { recorded_from: new Date(`${from}T00:00:00`).toISOString() } : {}),
    ...(to ? { recorded_to: nextMidnight(to) } : {}),
    limit: 100,
  });

  const filtered = !!siteId || !!status || !!from || !!to;

  const siteOptions = [
    { value: NONE, label: t("common.all") },
    ...(sitesQuery.data?.data ?? []).map((site) => ({ value: site.id, label: site.name })),
  ];

  const statusOptions = [
    { value: NONE, label: t("common.all") },
    { value: "completed", label: t("refills.status.completed") },
    { value: "pending", label: t("refills.status.pending") },
    { value: "cancelled", label: t("refills.status.cancelled") },
  ];

  return (
    <>
      <PageHeader
        title={t("refills.title")}
        actions={
          can("refills:create") ? (
            <Button asChild>
              <Link to="/refills/new">
                <Plus className="size-4" aria-hidden />
                {t("refills.new")}
              </Link>
            </Button>
          ) : null
        }
      />

      <Card>
        <CardHeader className="flex-wrap gap-3">
          {/* Every filter is labelled, so the four controls sit on one baseline instead of a
              ragged row of two bare selects and two labelled date fields. */}
          <div className="flex flex-wrap items-end gap-3">
            <Field label={t("refills.site")} className="w-52">
              {(props) => (
                <Select
                  {...props}
                  value={siteId || NONE}
                  onValueChange={(value) => setSiteId(value === NONE ? "" : value)}
                  options={siteOptions}
                  placeholder={t("common.all")}
                />
              )}
            </Field>

            <Field label={t("refills.status")} className="w-44">
              {(props) => (
                <Select
                  {...props}
                  value={status || NONE}
                  onValueChange={(value) => setStatus(value === NONE ? "" : value)}
                  options={statusOptions}
                  placeholder={t("common.all")}
                />
              )}
            </Field>

            <Field label={t("refills.dateFrom")} className="w-40">
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  dir="ltr"
                  value={from}
                  max={to || undefined}
                  className="h-10 font-mono text-xs"
                  onChange={(event) => setFrom(event.target.value)}
                />
              )}
            </Field>

            <Field label={t("refills.dateTo")} className="w-40">
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  dir="ltr"
                  value={to}
                  min={from || undefined}
                  className="h-10 font-mono text-xs"
                  onChange={(event) => setTo(event.target.value)}
                />
              )}
            </Field>

            {filtered ? (
              <Button
                variant="ghost"
                size="sm"
                className="mb-0.5"
                onClick={() => {
                  setSiteId("");
                  setStatus("");
                  setFrom("");
                  setTo("");
                }}
              >
                {t("common.clear")}
              </Button>
            ) : null}
          </div>
        </CardHeader>

        <RefillsTable
          query={refillsQuery}
          rowActions
          empty={
            <EmptyState
              icon={<Droplets className="size-6" />}
              title={t("refills.empty")}
              body={filtered ? t("refills.emptyFiltered") : t("refills.emptyBody")}
              action={
                filtered ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setSiteId("");
                      setStatus("");
                      setFrom("");
                      setTo("");
                    }}
                  >
                    {t("common.clear")}
                  </Button>
                ) : can("refills:create") ? (
                  <Button asChild size="sm">
                    <Link to="/refills/new">{t("refills.new")}</Link>
                  </Button>
                ) : null
              }
            />
          }
        />
      </Card>
    </>
  );
}

/* ------------------------------------------------- the arithmetic, shared */

const ISSUE_KEY: Record<RefillIssueCode, string> = {
  inconsistent_total: "refills.checkMismatch",
  over_capacity: "refills.checkOverCapacity",
  hours_backwards: "refills.checkHoursBackwards",
  negative: "refills.checkMismatch",
  not_a_number: "refills.checkMismatch",
};

/** "" → null, so an empty box is "not filled in", not zero. Zero is a legitimate value. */
const parse = (value: string): number | null => {
  if (value.trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : Number.NaN;
};

interface Figures {
  before: string;
  added: string;
  after: string;
  hours: string;
  setBefore: (value: string) => void;
  setAdded: (value: string) => void;
  setAfter: (value: string) => void;
  setHours: (value: string) => void;
  afterTouched: boolean;
  computedAfter: string;
  draft: RefillDraft;
  check: RefillCheck;
  canSubmit: boolean;
}

/**
 * The four figures of a refill, and the equation that binds them.
 *
 * Recording and amending are the same arithmetic — before + added = after, checked live, over
 * the same capacity. They share this hook so they can never drift apart: a rule tightened for
 * one is tightened for both, and an amendment cannot smuggle in a refill that the record form
 * would have refused.
 */
function useRefillFigures(
  context: RefillContext,
  initial: { before?: number; added?: number; after?: number; hours?: number } = {},
): Figures {
  const str = (n: number | undefined) => (n === undefined ? "" : String(n));

  const [before, setBefore] = useState(() => str(initial.before));
  const [added, setAdded] = useState(() => str(initial.added));
  const [after, setAfterRaw] = useState(() => str(initial.after));
  const [hours, setHours] = useState(() => str(initial.hours));

  // `after` is COMPUTED until the operator touches it. A stored refill already balances, so
  // an amendment opens with the computed value equal to the recorded one — and the moment
  // they correct `before` or `added`, the total re-solves instead of going stale.
  const [afterTouched, setAfterTouched] = useState(false);

  const computedAfter = useMemo(() => {
    const b = parse(before);
    const a = parse(added);
    if (b === null || a === null || Number.isNaN(b) || Number.isNaN(a)) return "";
    return String(Math.round((b + a) * 100) / 100);
  }, [before, added]);

  const effectiveAfter = afterTouched ? after : computedAfter;

  const draft: RefillDraft = {
    before_liters: parse(before),
    added_liters: parse(added),
    after_liters: parse(effectiveAfter),
    working_hours: parse(hours),
  };

  const check = checkRefill(draft, context);

  return {
    before,
    added,
    after: effectiveAfter,
    hours,
    setBefore,
    setAdded,
    setAfter: (value: string) => {
      setAfterTouched(true);
      setAfterRaw(value);
    },
    setHours,
    afterTouched,
    computedAfter,
    draft,
    check,
    canSubmit: isSubmittable(draft, context),
  };
}

/** Localize a broken invariant. The domain returns codes; only this layer speaks to people. */
function useFieldError(
  check: RefillCheck,
  context: RefillContext,
  serverErrors: Record<string, string>,
) {
  const { t, lang } = useI18n();

  /**
   * A client-side issue outranks a stale server one — it is about what is on screen NOW.
   *
   * `inconsistent_total` is deliberately NOT returned here: the live verdict strip states the
   * whole equation, and repeating the identical sentence under the input just says the same
   * thing twice in red.
   */
  return (field: RefillField): string | undefined => {
    const issue = issueFor(check, field);
    if (issue) {
      if (issue.code === "inconsistent_total") return undefined;
      if (issue.code === "over_capacity") {
        return t("refills.checkOverCapacity", {
          capacity: formatNumber(lang, context.capacityLiters),
        });
      }
      if (issue.code === "hours_backwards") {
        return t("refills.checkHoursBackwards", {
          current: formatNumber(lang, context.generatorRunningHours ?? 0, 1),
        });
      }
      return t(ISSUE_KEY[issue.code] as never);
    }
    return serverErrors[field];
  };
}

/**
 * before + added = after — computed as they type, shown as an equation, and an impossible
 * refill never reaches the server.
 */
function ArithmeticFields({
  figures,
  errorFor,
}: {
  figures: Figures;
  errorFor: (field: RefillField) => string | undefined;
}) {
  const { t, lang } = useI18n();
  const { check, draft } = figures;

  return (
    <fieldset className="rounded-[6px] border border-rule-strong bg-paper-sunk p-4">
      {/* `bg-paper-sunk` is load-bearing: a <legend> sits ON the fieldset's top border, and
          without a background of its own the hairline is drawn straight through the word. */}
      <legend className="label-caps bg-paper-sunk px-2 font-mono text-[10px] font-medium text-faint">
        {t("refills.arithmetic")}
      </legend>

      <div className="flex flex-wrap items-start gap-3">
        <Field
          label={t("refills.before")}
          error={errorFor("before_liters")}
          className="min-w-28 flex-1"
          required
        >
          {(props) => (
            <NumberInput
              {...props}
              required
              min={0}
              step="0.1"
              value={figures.before}
              onChange={(e) => figures.setBefore(e.target.value)}
            />
          )}
        </Field>

        <Plus className="mt-9 size-4 shrink-0 text-faint" aria-hidden />

        <Field
          label={t("refills.added")}
          error={errorFor("added_liters")}
          className="min-w-28 flex-1"
          required
        >
          {(props) => (
            <NumberInput
              {...props}
              required
              min={0}
              step="0.1"
              value={figures.added}
              onChange={(e) => figures.setAdded(e.target.value)}
            />
          )}
        </Field>

        <Equal className="mt-9 size-4 shrink-0 text-faint" aria-hidden />

        <Field
          label={t("refills.after")}
          error={errorFor("after_liters")}
          hint={
            !figures.afterTouched && figures.computedAfter ? t("refills.autoAfter") : undefined
          }
          className="min-w-28 flex-1"
          required
        >
          {(props) => (
            <NumberInput
              {...props}
              required
              min={0}
              step="0.1"
              value={figures.after}
              onChange={(e) => figures.setAfter(e.target.value)}
            />
          )}
        </Field>
      </div>

      {/* The verdict, in words, live. Not a red border they have to interpret. */}
      <div
        role="status"
        aria-live="polite"
        className={cn(
          "mt-3 flex items-center gap-2 text-xs font-medium",
          check.balances && check.valid ? "text-ok" : "text-faint",
        )}
      >
        {check.balances && check.valid ? (
          <>
            <Check className="size-3.5" aria-hidden />
            {t("refills.checkOk")}
          </>
        ) : draft.after_liters !== null && !check.balances ? (
          <>
            <X className="size-3.5 text-dry" aria-hidden />
            <span className="text-dry">
              {t("refills.checkMismatch", {
                before: formatNumber(lang, draft.before_liters ?? 0, 1),
                added: formatNumber(lang, draft.added_liters ?? 0, 1),
                expected: formatNumber(lang, check.expectedAfter ?? 0, 1),
                after: formatNumber(lang, draft.after_liters ?? 0, 1),
              })}
            </span>
          </>
        ) : null}
      </div>
    </fieldset>
  );
}

/* ------------------------------------------------------------- new refill */

export function NewRefillPage() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { siteId?: string; tankId?: string };
  const createRefill = useCreateRefill();

  const sitesQuery = useSites({ status: "active", limit: 100 });
  const sites = sitesQuery.data?.data ?? [];

  const [siteId, setSiteId] = useState(search.siteId ?? "");
  const [tankId, setTankId] = useState(search.tankId ?? "");
  const [generatorId, setGeneratorId] = useState("");

  const tanksQuery = useSiteTanks(siteId, { enabled: !!siteId });
  const generatorsQuery = useSiteGenerators(siteId, { enabled: !!siteId });

  const tanks = useMemo(() => tanksQuery.data?.data ?? [], [tanksQuery.data]);
  const generators = generatorsQuery.data?.data ?? [];

  // Arriving from the dashboard with a tank id but no site id: find the tank's site.
  useEffect(() => {
    if (!search.tankId || siteId) return;
    const site = sites.find((s) => s.id === search.siteId);
    if (site) setSiteId(site.id);
  }, [search.tankId, search.siteId, sites, siteId]);

  // One tank at the site? Then there is nothing to choose. Choose it.
  useEffect(() => {
    if (!tankId && tanks.length === 1) setTankId(tanks[0]!.id);
  }, [tanks, tankId]);

  const tank = tanks.find((t_) => t_.id === tankId);
  const generator = generators.find((g) => g.id === generatorId);

  const [notes, setNotes] = useState("");
  const [recordedAt, setRecordedAt] = useState(() => toDateTimeLocalValue(new Date()));

  const context: RefillContext = {
    capacityLiters: tank?.capacity_liters ?? 0,
    ...(generator ? { generatorRunningHours: generator.running_hours } : {}),
  };

  const figures = useRefillFigures(context);
  const { draft, check, setBefore } = figures;
  const canSubmit = !!tank && figures.canSubmit;

  // The tank knows what was in it. Pre-fill `before` — the operator confirms the figure
  // rather than recalling it, and a CORRECTED figure is itself a signal worth having.
  //
  // Keyed on the tank, and it fires exactly once per tank. Keying it on `before === ""`
  // instead — which is the obvious way to write it — means that the moment the operator
  // clears the box to type their own reading, the effect sees an empty field and puts the
  // old number straight back. They then type into a field that is not empty and get
  // "19001850". The dipstick says otherwise, and the form fights them.
  const prefilledForTank = useRef<string | null>(null);
  useEffect(() => {
    if (!tank || prefilledForTank.current === tank.id) return;
    prefilledForTank.current = tank.id;
    setBefore(
      tank.current_level_liters !== undefined ? String(tank.current_level_liters) : "",
    );
  }, [tank, setBefore]);

  const serverErrors =
    createRefill.error instanceof ApiError ? createRefill.error.fieldErrors : {};
  const errorFor = useFieldError(check, context, serverErrors);

  const submit = () => {
    if (!tank || !canSubmit) return;
    createRefill.mutate(
      {
        tank_id: tank.id,
        ...(generator ? { generator_id: generator.id } : {}),
        before_liters: draft.before_liters!,
        added_liters: draft.added_liters!,
        after_liters: draft.after_liters!,
        working_hours: draft.working_hours!,
        recorded_at: new Date(recordedAt).toISOString(),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        status: "completed",
      },
      { onSuccess: () => void navigate({ to: "/refills" }) },
    );
  };

  return (
    <>
      <PageHeader title={t("refills.new")} />

      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <Card>
          <CardBody>
            <form
              className="flex flex-col gap-5"
              onSubmit={(event) => {
                event.preventDefault();
                submit();
              }}
              noValidate
            >
              <FormError
                error={
                  createRefill.error instanceof ApiError && createRefill.error.isFormLevel
                    ? createRefill.error
                    : null
                }
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t("refills.site")} required>
                  {(props) => (
                    <Select
                      {...props}
                      value={siteId}
                      onValueChange={(value) => {
                        setSiteId(value);
                        setTankId("");
                        setGeneratorId("");
                        setBefore("");
                      }}
                      options={sites.map((s) => ({
                        value: s.id,
                        label: s.name,
                        ...(s.code ? { hint: s.code } : {}),
                      }))}
                      placeholder={t("refills.selectSite")}
                    />
                  )}
                </Field>

                <Field label={t("refills.tank")} required>
                  {(props) => (
                    <Select
                      {...props}
                      value={tankId}
                      onValueChange={(value) => {
                        setTankId(value);
                        setBefore("");
                      }}
                      disabled={!siteId || tanks.length === 0}
                      options={tanks.map((tk) => ({
                        value: tk.id,
                        label: tk.name,
                        hint: `${formatLiters(lang, tk.capacity_liters)} ${t("common.liters")}`,
                      }))}
                      placeholder={t("refills.selectTank")}
                    />
                  )}
                </Field>
              </div>

              <Field label={t("refills.generator")} hint={t("common.optional")}>
                {(props) => (
                  <Select
                    {...props}
                    value={generatorId || NONE}
                    onValueChange={(value) => setGeneratorId(value === NONE ? "" : value)}
                    disabled={!siteId}
                    options={[
                      { value: NONE, label: t("refills.selectGenerator") },
                      ...generators
                        .filter((g) => g.status !== "decommissioned")
                        .map((g) => ({
                          value: g.id,
                          label: g.name,
                          hint: `${formatHours(lang, g.running_hours)} ${t("common.hours")}`,
                        })),
                    ]}
                  />
                )}
              </Field>

              <ArithmeticFields figures={figures} errorFor={errorFor} />

              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label={t("refills.workingHours")}
                  error={errorFor("working_hours")}
                  hint={
                    generator
                      ? `${t("gen.runningHours")}: ${formatHours(lang, generator.running_hours)}`
                      : undefined
                  }
                  required
                >
                  {(props) => (
                    <NumberInput
                      {...props}
                      required
                      min={0}
                      step="0.1"
                      value={figures.hours}
                      onChange={(e) => figures.setHours(e.target.value)}
                    />
                  )}
                </Field>

                <Field label={t("refills.recordedAt")} required>
                  {(props) => (
                    <input
                      {...props}
                      type="datetime-local"
                      required
                      dir="ltr"
                      value={recordedAt}
                      max={toDateTimeLocalValue(new Date())}
                      onChange={(e) => setRecordedAt(e.target.value)}
                      className="h-10 w-full rounded-[6px] border border-rule-strong bg-paper px-3 font-mono text-sm text-text focus-visible:border-ink focus-visible:outline-2 focus-visible:outline-offset-[-1px] focus-visible:outline-ink"
                    />
                  )}
                </Field>
              </div>

              <Field label={t("refills.notes")} hint={t("common.optional")}>
                {(props) => (
                  <Textarea
                    {...props}
                    value={notes}
                    placeholder={t("refills.notesPlaceholder")}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                )}
              </Field>

              <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" onClick={() => void navigate({ to: "/refills" })}>
                  {t("common.cancel")}
                </Button>
                {/* Disabled until the arithmetic closes. An impossible refill is never sent. */}
                <Button type="submit" disabled={!canSubmit || createRefill.isPending}>
                  {createRefill.isPending ? t("common.saving") : t("refills.new")}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>

        {/* The tank, as it will be. The glass fills as they type — the arithmetic made physical. */}
        <Card className="h-fit">
          <CardHeader>
            <CardTitle>{tank?.name ?? t("refills.selectTank")}</CardTitle>
          </CardHeader>
          <CardBody className="flex flex-col items-center gap-4">
            <SightGlass
              percent={check.fillFraction !== null ? check.fillFraction * 100 : null}
              level={
                check.fillFraction !== null && check.fillFraction * 100 <= 20 ? "low" : "ok"
              }
              className="h-44 w-12"
            />

            {tank ? (
              <div className="w-full text-center">
                <p className="font-mono text-2xl text-text">
                  <Num>
                    {draft.after_liters !== null && !Number.isNaN(draft.after_liters)
                      ? formatLiters(lang, draft.after_liters)
                      : "—"}
                  </Num>
                </p>
                <MicroLabel>
                  {t("common.of")} {formatLiters(lang, tank.capacity_liters)} {t("common.liters")}
                </MicroLabel>

                {tank.current_level_liters !== undefined ? (
                  <p className="mt-3 border-t border-rule pt-3 text-xs text-faint">
                    {t("refills.tankLevelNow", {
                      liters: formatLiters(lang, tank.current_level_liters),
                    })}
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="text-center text-xs text-faint">{t("refills.selectTank")}</p>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}
